import React from 'react';
import moment from 'moment-timezone';
import Button from 'components/Button';
import CoachingConstants from 'coaching/CoachingConstants';
import CoachingHeading from 'coaching/components/CoachingHeading';
import { CoachingReservation, CoachingTimeOff } from 'coaching/containers/GetAvailableReservationsContainer';
import CoachingSelfServeTimeOffView from "coaching/components/CoachingSelfServeView/CoachingSelfServeTimeOffView";
import Copy from 'components/Copy';
import DateTimeAvailabilitySelect from 'components/DateTimeAvailabilitySelect';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import TimezoneHelper from 'lib/timezones';
import ToastActions from 'actions/ToastActions';

type Props = {
  selectedSubject: string;
  disabledContinueButton: boolean;
  daysToShow: moment.Moment[];
  userTimezone: string;
  selectedReservation?: CoachingReservation;
  reservations?: CoachingReservation[];
  timeOff?: CoachingTimeOff[];
  loading: boolean;
  error?: string;
  onSelectReservation: (reservation: CoachingReservation) => void;
  onSelectTimezone: (tz: string) => void;
  onContinue: () => void;
};

type State = {};

export default class CoachingSelfServeScheduleSessionView extends React.Component<Props, State> {
  componentDidMount() {
    this.trackEvent('view');
  }

  eventPrefix = 'coaching.self_schedule_purchase.schedule_session';

  trackEvent(key: string) {
    EventTracking.trackKey(`${this.eventPrefix}.${key}`);
  }

  handleSelectDate = (date: moment.Moment) => {
    this.trackEvent('time.click');

    const errorToastText =
      'Oops! Looks like something went wrong - please refresh the page and try again.';
    const { reservations } = this.props;
    if (!reservations) {
      ToastActions.showErrorToast(errorToastText);
      console.error(
        'CoachingSelfServeScheduleSessionView - tried to select a date without any reservations loaded'
      );
      return;
    }

    const selectedReservation = reservations.find(res =>
      moment.utc(res.startDateTime).isSame(date, 'second')
    );
    if (!selectedReservation) {
      ToastActions.showErrorToast(errorToastText);
      console.error(
        'CoachingSelfServeScheduleSessionView - tried to select a date without a corresponding reservation'
      );
      return;
    }

    this.props.onSelectReservation(selectedReservation);
  };

  render() {
    const {
      onContinue,
      disabledContinueButton,
      daysToShow,
      reservations,
      error,
      selectedReservation,
      onSelectTimezone,
      userTimezone,
      loading,
      timeOff
    } = this.props;
    if (error) {
      return (
        <ErrorReporter
          error={`CoachingSelfServeScheduleSessionView - could not load reservations e=${error}`}>
          Oops, looks like something went wrong - please try refreshing the page.
        </ErrorReporter>
      );
    }

    const dateTimeOptionsInUserTimezone =
      reservations?.map(r => r.startDateTime.tz(userTimezone)) ?? [];

    return (
      <React.Fragment>
        <CoachingHeading>Schedule your session</CoachingHeading>
        <Row>
          <Copy align="left" size="small">
            This full-length session will be 40 minutes. Coaching is available Monday through
            Thursday after school.
          </Copy>
        </Row>
        <Row size="xsmall">
          <MediaObject center gutter="xsmall">
            <Text color="thunder">Times shown in</Text>
            <Select
              inline
              value={userTimezone}
              onChange={(tz: string) => {
                this.trackEvent('timezone.click');
                onSelectTimezone(tz);
              }}
              options={TimezoneHelper.USTimezoneSelectOptions.filter(
                option => !CoachingConstants.TIMEZONES_TO_EXCLUDE.includes(option.label)
              )}
            />
          </MediaObject>
        </Row>
        <Row>
          <DateTimeAvailabilitySelect
            data-test="reservation-date-time-select"
            value={selectedReservation?.startDateTime}
            onChange={this.handleSelectDate}
            daysToShow={daysToShow}
            dateTimeOptions={dateTimeOptionsInUserTimezone}
            onSelectDay={() => this.trackEvent('date.click')}
            onMoreAvailabilities={() => this.trackEvent('show_more.click')}
            onLessAvailabilities={() => this.trackEvent('show_less.click')}
            onEmptyDay={(day: string) => {
              if (!loading) {
                console.error(
                  `CoachingSelfServeScheduleSessionView - could not find any open slots on ${day} for ${this.props.selectedSubject}`
                );
              }
            }}
          />
        </Row>
        {timeOff && <CoachingSelfServeTimeOffView timeOff={timeOff} />}
        <Button
          data-test="schedule-session-continue-button"
          color="koolaid"
          eventKey={`${this.eventPrefix}.continue.click`}
          onClick={onContinue}
          disabled={disabledContinueButton}>
          Select
        </Button>
      </React.Fragment>
    );
  }
}
